'use client'

import { ChainType } from '@polaris/core/models'
import {
  formatCoin,
  formatPrice,
  formatRate,
  shorten,
} from '@polaris/core/utils'
import { ClientOnly } from '@quirks/react'
import { useAtomValue } from 'jotai'
import { ChevronsDownUp, ChevronsUpDown, Send, Wallet } from 'lucide-react'
import { motion } from 'motion/react'
import type {
  DetailedHTMLProps,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from 'react'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { useIsInRoutePage } from '@/src/hooks/use-is-in-route-page'
import {
  additionalFeesTextAtom,
  destinationChainAccountAtom,
  destinationChainAddressAtom,
  isAbortingAtom,
  isCompleteAtom,
  latestSignedStepAtom,
  recoverAccountAtom,
  routeAtom,
  routeMachineSnapshotAtom,
  sourceChainAccountAtom,
  sourceChainAddressAtom,
} from '@/src/stores/route-machine/atoms'
import {
  DEFAULT_SLIPPAGE,
  priceImpactAtom,
  routeErrorsAtom,
} from '@/src/stores/swap/atoms'
import { tradeStepAtom } from '@/src/stores/trade/atoms'
import { cn } from '@/src/utils/styles'
import { WalletIcon } from '../accounts/wallet-icon'
import { WatchAddressIcon } from '../accounts/watch-address'
import TransferringDisclaimer from '../trade-overview/transferring-disclaimer'
import { Badge } from '../ui/badge'
import { Button, ButtonDiv } from '../ui/button'
import { ChainImg } from '../ui/chain-img'
import CopyButton from '../ui/copy-button'
import TokenImg from '../ui/token-img'

// Route screen is rendered at parent level; this component triggers it via callback

interface ExpandedBodyRowProps
  extends Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    'title'
  > {
  title: ReactNode
}

function ExpandedBodyRow(props: PropsWithChildren<ExpandedBodyRowProps>) {
  const { title, ...rest } = props

  return (
    <div
      className='flex items-start justify-between w-full min-h-8 py-1 text-primary text-sm'
      {...rest}
    >
      <span className='text-secondary'>{title}</span>
      {props.children}
    </div>
  )
}

function ExpandedBodyHeader() {
  const route = useAtomValue(routeAtom)
  const isComplete = useAtomValue(isCompleteAtom)
  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const fromAccount = useAtomValue(sourceChainAccountAtom)
  const fromWalletAddress = useAtomValue(sourceChainAddressAtom)

  const toAccount = useAtomValue(destinationChainAccountAtom)
  const toManualAddress = !toAccount
    ? route?.finalSwapAddressOverwrite
    : undefined
  const toWalletAddress = useAtomValue(destinationChainAddressAtom)

  const isSameWallet =
    fromWalletAddress &&
    toWalletAddress &&
    fromWalletAddress === toWalletAddress

  if (!route) return null

  return (
    <>
      <ExpandedBodyRow title={isComplete ? 'Paid' : 'Pay'}>
        <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
          <span className='text-primary text-sm'>
            {route.AmountIn} {route.InputAssetMetadata.ticker}
          </span>
          <span className='text-secondary text-sm'>
            ({formatPrice(route.InputAssetUSDPrice * route.AmountIn)})
          </span>
        </div>
      </ExpandedBodyRow>
      {snapshot.value !== 'FundsRecoverySuccess' && (
        <ExpandedBodyRow
          title={isComplete ? 'Received' : 'Receive (estimated)'}
        >
          <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
            <span className='text-primary text-sm'>
              {formatCoin(
                {
                  amount: route.AmountOut,
                  denom: route.OutputAssetMetadata.ticker,
                },
                {
                  maximumFractionDigits: route.OutputAssetMetadata.precision,
                  maximumSignificantDigits: 6,
                },
              )}
            </span>
            <span className='text-secondary text-sm'>
              ({formatPrice(route.usdValueAmountOut)})
            </span>
          </div>
        </ExpandedBodyRow>
      )}
      <ClientOnly>
        {fromAccount && (
          <ExpandedBodyRow title={isSameWallet ? 'Wallet' : 'From'}>
            <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
              {fromAccount.provider_info.img === 'no-logo' ? (
                <WatchAddressIcon size='xs' className='rounded-[6px]' />
              ) : fromAccount.provider_info.img ? (
                <WalletIcon
                  logoUrl={fromAccount.provider_info.img}
                  walletName={fromAccount.provider_info.name || ''}
                  size={24}
                  className='size-6'
                  imageClassName='opacity-100'
                />
              ) : null}
              {fromWalletAddress && (
                <span className='text-primary text-sm'>
                  {shorten(fromWalletAddress)}
                </span>
              )}
            </div>
          </ExpandedBodyRow>
        )}
      </ClientOnly>
      {!isSameWallet && (
        <ClientOnly>
          {toAccount && (
            <ExpandedBodyRow title='To'>
              <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                {toAccount.provider_info.img === 'no-logo' ? (
                  <WatchAddressIcon size='xs' className='rounded-[6px]' />
                ) : toAccount.provider_info.img ? (
                  <WalletIcon
                    logoUrl={toAccount.provider_info.img}
                    walletName={toAccount.provider_info.name || ''}
                    size={24}
                    className='size-6'
                    imageClassName='opacity-100'
                  />
                ) : null}
                {toWalletAddress && (
                  <span className='text-primary text-sm'>
                    {shorten(toWalletAddress)}
                  </span>
                )}
              </div>
            </ExpandedBodyRow>
          )}
          {toManualAddress && (
            <ExpandedBodyRow title='To'>
              <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                <div className='flex items-center justify-center size-6 min-w-6 rounded-[6px] bg-orange-200'>
                  <Send className='size-4 text-green-800' size={16} />
                </div>
                {toManualAddress && (
                  <span className='text-primary text-sm'>
                    {shorten(toManualAddress)}
                  </span>
                )}
              </div>
            </ExpandedBodyRow>
          )}
        </ClientOnly>
      )}
    </>
  )
}

export default function ExpandedBody({
  onOpenRoute,
  showAutopilotSettings,
}: {
  onOpenRoute?: () => void
  showAutopilotSettings?: () => void
}) {
  const route = useAtomValue(routeAtom)
  const isComplete = useAtomValue(isCompleteAtom)
  const routeErrors = useAtomValue(routeErrorsAtom)
  const priceImpact = useAtomValue(priceImpactAtom)
  const additionalFeesText = useAtomValue(additionalFeesTextAtom)
  const latestSignedStep = useAtomValue(latestSignedStepAtom)
  const tradeStep = useAtomValue(tradeStepAtom)
  const associatedRouteStep = latestSignedStep
    ? route?.Steps.find((s) => s.step_number === latestSignedStep.stepNumber)
    : undefined
  const txHash = latestSignedStep?.signState?.txHash

  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const isAborting = useAtomValue(isAbortingAtom)
  const recoveryAccount = useAtomValue(recoverAccountAtom)
  const isInRoutePage = useIsInRoutePage()

  const { onToggle: onToggleExpandDetails, isOpen: isDetailsExpanded } =
    useDisclosure(isInRoutePage)

  if (!route) return null

  const maxSlippage = route.Steps[0].slippage.max

  /**
   * There is an autopilot runner derived just for this route,
   * so if the route failed we should display settings to allow recovery.
   */
  const isFailedRouteDerivedAutopilot =
    (Boolean(routeErrors) || route.isFailed) &&
    route.sourceChain.chain_type === ChainType.Namada

  if (isAborting) {
    const refundInfo = snapshot.context.refundInfo
    const failedStep = snapshot.context.route!.getStep(
      snapshot.context.currentStep,
    )

    // refundInfo comes from BE and is the automated autopilot recovery data
    const resolvedRecoverAddress = refundInfo?.destinationAddress

    const displayAmount = refundInfo?.amount
    const displayTicker = refundInfo?.ticker ?? refundInfo?.denom
    const displayPrecision = refundInfo?.precision
    const displayTokenImg = refundInfo?.assetImg

    const networkChainId = refundInfo?.chainID ?? failedStep.from_chain.chain_id
    const networkChainName =
      refundInfo?.chainName ?? failedStep.from_chain.chain_name
    const networkChainImg =
      refundInfo?.chainImg ?? failedStep.from_chain.chain_img

    const stepWalletLogo = recoveryAccount?.provider_info.img
    const stepWalletName = recoveryAccount?.provider_info.name

    return (
      <div
        className={cn('flex flex-col px-5 gap-1 overflow-hidden', {
          'pt-5 border-t border-border1': !isComplete,
        })}
      >
        <motion.div
          initial={{
            opacity: 0,
            height: '0px',
          }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          {displayAmount && displayTicker && (
            <ExpandedBodyRow title={'Send'}>
              <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                <TokenImg
                  src={displayTokenImg}
                  ticker={displayTicker}
                  width={24}
                  height={24}
                  className='size-6 rounded-full'
                  fallbackContainerClassName='size-6'
                  unoptimized
                />
                <span className='text-primary text-sm'>
                  {formatCoin(
                    {
                      amount: Number(displayAmount),
                      denom: displayTicker,
                    },
                    {
                      maximumFractionDigits: displayPrecision ?? 6,
                      maximumSignificantDigits: 6,
                    },
                  )}{' '}
                </span>
              </div>
            </ExpandedBodyRow>
          )}
          <ExpandedBodyRow title='Network'>
            <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
              <ChainImg
                chainId={networkChainId}
                chainImg={networkChainImg}
                chainName={networkChainName}
                unoptimized
              />
              <span>{networkChainName}</span>
            </div>
          </ExpandedBodyRow>
          {resolvedRecoverAddress && (
            <ExpandedBodyRow title='To'>
              <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                {stepWalletLogo === 'no-logo' ? (
                  <WatchAddressIcon size='xs' className='rounded-[6px]' />
                ) : stepWalletLogo ? (
                  <WalletIcon
                    logoUrl={stepWalletLogo}
                    walletName={stepWalletName || ''}
                    size={24}
                    className='size-6'
                  />
                ) : (
                  <Wallet className='text-secondary size-6' />
                )}
                <span>{shorten(resolvedRecoverAddress)}</span>
              </div>
            </ExpandedBodyRow>
          )}

          <ExpandedBodyRow
            title='Trade ID'
            data-testid='trx-id'
            data-trxid={route.ID}
          >
            <CopyButton
              contentToCopy={route.ID}
              displayContent={shorten(route.ID)}
              className='translate-x-3'
            />
          </ExpandedBodyRow>

          {isComplete && (
            <ExpandedBodyRow title='Route'>
              <Button variant='ghost' size='sm' onClick={() => onOpenRoute?.()}>
                Show route
              </Button>
            </ExpandedBodyRow>
          )}
        </motion.div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col')}>
      {tradeStep.isLoading && !snapshot.context.shouldUseMpc && (
        <TransferringDisclaimer />
      )}
      <div
        className={cn('flex flex-col px-5 gap-1 overflow-hidden', {
          'pt-5 border-t border-border1': !isComplete,
        })}
      >
        {!isComplete && <ExpandedBodyHeader />}
        <motion.div
          initial={{
            opacity: isInRoutePage ? 1 : 0,
            height: isInRoutePage ? 'auto' : '0px',
          }}
          animate={{
            height: isDetailsExpanded ? 'auto' : '0px',
            opacity: isDetailsExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.25 }}
        >
          <div className='flex flex-col gap-1'>
            {isComplete && <ExpandedBodyHeader />}
            {route.sourceChain.chain_id === route.destinationChain.chain_id ? (
              <ExpandedBodyRow title='Network'>
                <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                  <span className='text-primary text-sm'>
                    {route.sourceChain.chain_name}
                  </span>
                </div>
              </ExpandedBodyRow>
            ) : (
              <ExpandedBodyRow title='Networks'>
                <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                  <span className='text-primary text-sm'>
                    {route.sourceChain.chain_name}
                  </span>
                  <span className='text-tertiary-100 text-sm'>to</span>
                  <span className='text-primary text-sm'>
                    {route.destinationChain.chain_name}
                  </span>
                </div>
              </ExpandedBodyRow>
            )}
            {!isComplete && (
              <>
                <ExpandedBodyRow title='Slippage Tolerance'>
                  <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                    {maxSlippage === +DEFAULT_SLIPPAGE && (
                      <Badge className='font-normal rounded-xl py-1 px-2 text-xs h-6'>
                        Auto
                      </Badge>
                    )}
                    <span className='text-sm text-secondary-100'>
                      {maxSlippage}%
                    </span>
                  </div>
                </ExpandedBodyRow>
                <ExpandedBodyRow title='Price impact'>
                  <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                    <span
                      className={`text-sm ${priceImpact && priceImpact < -0.1 ? 'text-danger-text' : 'text-primary'}`}
                    >
                      {formatRate(priceImpact || 0)}
                    </span>
                  </div>
                </ExpandedBodyRow>
              </>
            )}
            {isComplete && (
              <ExpandedBodyRow title='Route'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => onOpenRoute?.()}
                >
                  Show route
                </Button>
              </ExpandedBodyRow>
            )}
            {!isComplete && route.minReceived && (
              <ExpandedBodyRow title='Receive at least'>
                <span className='text-primary text-sm'>
                  {formatCoin(
                    {
                      amount: route.minReceived,
                      denom: route.OutputAssetMetadata.ticker,
                    },
                    {
                      maximumFractionDigits:
                        route.OutputAssetMetadata.precision,
                      maximumSignificantDigits: 6,
                    },
                  )}{' '}
                  <span className='text-secondary'>
                    ({formatPrice(route.minReceivedUsd)})
                  </span>
                </span>
              </ExpandedBodyRow>
            )}
            <ExpandedBodyRow title='Total fees'>
              <div className='flex items-center gap-1.5 justify-end text-right flex-wrap'>
                <p className='text-primary text-sm'>{additionalFeesText}</p>
              </div>
            </ExpandedBodyRow>
            <ExpandedBodyRow
              title='Trade ID'
              data-testid='trx-id'
              data-trxid={route.ID}
            >
              <CopyButton
                contentToCopy={route.ID}
                displayContent={shorten(route.ID)}
              />
            </ExpandedBodyRow>
            {isFailedRouteDerivedAutopilot && (
              <ExpandedBodyRow
                title='Autopilot settings'
                data-testid='trx-id'
                data-trxid={route.ID}
              >
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => showAutopilotSettings?.()}
                >
                  View autopilot settings
                </Button>
              </ExpandedBodyRow>
            )}
            {txHash && (
              <ExpandedBodyRow
                title={`${associatedRouteStep?.from_chain.chain_name} transaction hash`}
              >
                <CopyButton
                  contentToCopy={txHash}
                  displayContent={shorten(txHash)}
                />
              </ExpandedBodyRow>
            )}
          </div>
        </motion.div>
        {!isInRoutePage && (
          <button
            type='button'
            onClick={onToggleExpandDetails}
            className='flex relative z-10 items-center justify-between group h-16 focus-inner'
          >
            <div className='h-[1px] bg-border1 w-full' />
            <ButtonDiv variant='outline' size='sm'>
              <span className='whitespace-nowrap text-sm font-semibold'>
                {isDetailsExpanded ? 'Hide details' : 'Show details'}
              </span>
              {isDetailsExpanded ? (
                <ChevronsDownUp size={16} />
              ) : (
                <ChevronsUpDown size={16} />
              )}
            </ButtonDiv>
            <div className='h-[1px] bg-border1 w-full' />
          </button>
        )}
      </div>
    </div>
  )
}
